import * as React from 'react';
import classnames from 'classnames';
import isEmpty from 'lodash/isEmpty';

import Entity from 'components/entity/Entity';
import EntityAvatar from 'components/entity/entity-avatar/EntityAvatar';
import Tooltip from 'components/Tooltip';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import OutfitCreatorHandle from '../outfit-creator-handle/CcmCampaignReportingOutfitCreatorHandle';

import { __t, applyTranslations } from 'utils/language';
import { abbrNum, getPercentage } from 'utils/helper/common';
import { renderPrice } from 'utils/helper/price';
import { getSortingFunction } from './ccm-campaign-reporting';

applyTranslations(require('../table/ccm-campaign-reporting-table.i18n.json'));

import type {
  SortableTableColumnType
} from 'types';
import type {
  CcmCampaignReportingOutfitPerformanceAll
} from 'modules/campaigns/components/ccm/types/CcmCampaignReporting.type';

const MSG_KEY = 'ccm_campaign_reporting_table';

const abbrNumMetric = (
  value: number
): string => (
  abbrNum(value, { decimal: 1 }, true)
);

const getMetricTitle = (
  section: string,
  key: string,
  withTooltip: boolean = true
): React.ReactNode => {
  const text = __t(`${MSG_KEY}.headers.${section}.${key}`);

  if (!withTooltip) {
    return (
      <span>
        {text}
      </span>
    );
  }

  return (
    <Tooltip
      width="normal"
      textAlign="center"
      message={__t(`${MSG_KEY}.tooltips.${key}`)}
    >
      {text}
    </Tooltip>
  );
};


export const getPerformanceByCountryColumns = (): Array<SortableTableColumnType> => ([
  {
    key: 'countryCode',
    title: getMetricTitle('country', 'country_code', false),
    width: 264,
    align: 'center',
    sort: (
      a: CcmCampaignReportingOutfitPerformanceAll,
      b: CcmCampaignReportingOutfitPerformanceAll
    ) => (
      getSortingFunction()?.countryCode(a, b)
    ),
    render: (col: CcmCampaignReportingOutfitPerformanceAll) => (
      <strong>
        {col.countryCode.toUpperCase()}
      </strong>
    )
  },
  {
    key: 'totalBudgetSpent',
    title: getMetricTitle('country', 'total_budget_spent'),
    width: 264,
    align: 'center',
    sort: (
      a: CcmCampaignReportingOutfitPerformanceAll,
      b: CcmCampaignReportingOutfitPerformanceAll
    ) => (
      getSortingFunction()?.totalBudgetSpent(a, b)
    ),
    render: (col: CcmCampaignReportingOutfitPerformanceAll) => (
      renderPrice(col.totalBudgetSpent)
    )
  },
  {
    key: 'liveOutfits',
    title: getMetricTitle('country', 'live_outfits'),
    width: 96,
    align: 'center',
    sort: (
      a: CcmCampaignReportingOutfitPerformanceAll,
      b: CcmCampaignReportingOutfitPerformanceAll
    ) => (
      getSortingFunction()?.liveOutfits(a, b)
    ),
    render: (col: CcmCampaignReportingOutfitPerformanceAll) => (
      abbrNumMetric(col.liveOutfits)
    )
  },
  {
    key: 'outfitsViews',
    title: getMetricTitle('country', 'outfits_views'),
    width: 96,
    align: 'center',
    sort: (
      a: CcmCampaignReportingOutfitPerformanceAll,
      b: CcmCampaignReportingOutfitPerformanceAll
    ) => (
      getSortingFunction()?.outfitsViews(a, b)
    ),
    render: (col: CcmCampaignReportingOutfitPerformanceAll) => (
      abbrNumMetric(col.outfitsViews)
    )
  },
  {
    key: 'outfitsClicks',
    title: getMetricTitle('country', 'outfits_clicks'),
    width: 96,
    align: 'center',
    sort: (
      a: CcmCampaignReportingOutfitPerformanceAll,
      b: CcmCampaignReportingOutfitPerformanceAll
    ) => (
      getSortingFunction()?.outfitsClicks(a, b)
    ),
    render: (col: CcmCampaignReportingOutfitPerformanceAll) => (
      abbrNumMetric(col.outfitsClicks)
    )
  },
  {
    key: 'outfitsCtr',
    title: getMetricTitle('country', 'outfits_ctr'),
    width: 96,
    align: 'center',
    sort: (
      a: CcmCampaignReportingOutfitPerformanceAll,
      b: CcmCampaignReportingOutfitPerformanceAll
    ) => (
      getSortingFunction()?.outfitsCtr(a, b)
    ),
    render: (col: CcmCampaignReportingOutfitPerformanceAll) => (
      `${getPercentage(col.outfitsCtr, 1)}%`
    )
  },
  {
    key: 'outfitsProductPageViews',
    title: getMetricTitle('country', 'outfits_product_page_views'),
    width: 96,
    align: 'center',
    sort: (
      a: CcmCampaignReportingOutfitPerformanceAll,
      b: CcmCampaignReportingOutfitPerformanceAll
    ) => (
      getSortingFunction()?.outfitsProductPageViews(a, b)
    ),
    render: (col: CcmCampaignReportingOutfitPerformanceAll) => (
      abbrNumMetric(col.outfitsProductPageViews)
    )
  },
  {
    key: 'outfitsNumItemsSold',
    title: getMetricTitle('country', 'outfits_num_items_sold'),
    width: 96,
    align: 'center',
    sort: (
      a: CcmCampaignReportingOutfitPerformanceAll,
      b: CcmCampaignReportingOutfitPerformanceAll
    ) => (
      getSortingFunction()?.outfitsNumItemsSold(a, b)
    ),
    render: (col: CcmCampaignReportingOutfitPerformanceAll) => (
      abbrNumMetric(col.outfitsNumItemsSold)
    )
  },
  {
    key: 'outfitsRevenue',
    title: getMetricTitle('country', 'outfits_revenue'),
    width: 96,
    align: 'center',
    sort: (
      a: CcmCampaignReportingOutfitPerformanceAll,
      b: CcmCampaignReportingOutfitPerformanceAll
    ) => (
      getSortingFunction()?.outfitsRevenue(a, b)
    ),
    render: (col: CcmCampaignReportingOutfitPerformanceAll) => (
      renderPrice(col.outfitsRevenue)
    )
  }
]);

export const getPerformanceByCountryOutfits = (): Array<SortableTableColumnType> => ([
  {
    key: 'outfitDetails',
    title: getMetricTitle('outfit', 'outfit_details', false),
    width: 456,
    align: 'center',
    sort: (
      a: CcmCampaignReportingOutfitPerformanceAll,
      b: CcmCampaignReportingOutfitPerformanceAll
    ) => (
      getSortingFunction()?.outfitDetails(a, b)
    ),
    render: (col: CcmCampaignReportingOutfitPerformanceAll, _, isRowExpanded?: boolean) => {
      const outfitDetails = col.outfitDetails;
      const outfitEntity = {
        id: outfitDetails.id,
        firstName: outfitDetails.outfitName,
        lastName: '',
        image: { url: outfitDetails.imageUrl },
        displayName: ''
      };

      const iconClasses = classnames('ccm-campaign-reporting-table__outfit-details-icon', {
        'ccm-campaign-reporting-table__outfit-details-icon--expanded': isRowExpanded,
        'ccm-campaign-reporting-table__outfit-details-icon--dynamic': !isEmpty(col.outfitDetails?.items)
      });

      return (
        <div className="ccm-campaign-reporting-table__outfit-details-container">
          <div className="ccm-campaign-reporting-table__outfit-details">
            <div className="ccm-campaign-reporting-table__outfit-details-entity">
              <div className={iconClasses}>
                <SVGDynamicIcon
                  clickable
                  name="chevron-down-custom-fg"
                  customColor="grey"
                  width={16}
                  height={16}
                />
              </div>
              <Entity>
                <EntityAvatar
                  entity={outfitEntity}
                  bordered
                  form="square"
                  size="customSize"
                  customSize={{
                    width: 56,
                    height: 56
                  }}
                />
              </Entity>
            </div>
            <div className="ccm-campaign-reporting-table__outfit-details-meta text-overflow-ellipsis">
              <div
                className="ccm-campaign-reporting-table__outfit-details-meta-main text-overflow-ellipsis"
                title={outfitDetails.outfitName}
              >
                {outfitDetails.outfitName}
              </div>
              <OutfitCreatorHandle
                className="ccm-campaign-reporting-table__outfit-details-meta-sub"
                handle={outfitDetails.creatorHandle}
              />
            </div>
          </div>
        </div>
      );
    }
  },
  {
    key: 'outfitPreview',
    title: getMetricTitle('outfit', 'preview', false),
    width: 80,
    align: 'center',
    render: (col: CcmCampaignReportingOutfitPerformanceAll) => (
      <a
        href={col.outfitPreview}
        target="_blank"
        rel="noopener noreferrer"
      >
        <SVGDynamicIcon
          name="link-external-custom-fg"
          customColor="grey"
          width={17}
          height={17}
          onClick={(event) => event.stopPropagation()}
        />
      </a>
    )
  },
  {
    key: 'brandedSku',
    title: getMetricTitle('outfit', 'branded_sku'),
    width: 96,
    align: 'center',
    sort: (
      a: CcmCampaignReportingOutfitPerformanceAll,
      b: CcmCampaignReportingOutfitPerformanceAll
    ) => (
      getSortingFunction()?.brandedSku(a, b)
    ),
    render: (col: CcmCampaignReportingOutfitPerformanceAll) => (
      abbrNumMetric(col.brandedSku)
    )
  },
  {
    key: 'outfitsViews',
    title: getMetricTitle('country', 'outfits_views'),
    width: 96,
    align: 'center',
    sort: (
      a: CcmCampaignReportingOutfitPerformanceAll,
      b: CcmCampaignReportingOutfitPerformanceAll
    ) => (
      getSortingFunction()?.outfitsViews(a, b)
    ),
    render: (col: CcmCampaignReportingOutfitPerformanceAll) => (
      abbrNumMetric(col.outfitsViews)
    )
  },
  {
    key: 'outfitsClicks',
    title: getMetricTitle('country', 'outfits_clicks'),
    width: 96,
    align: 'center',
    sort: (
      a: CcmCampaignReportingOutfitPerformanceAll,
      b: CcmCampaignReportingOutfitPerformanceAll
    ) => (
      getSortingFunction()?.outfitsClicks(a, b)
    ),
    render: (col: CcmCampaignReportingOutfitPerformanceAll) => (
      abbrNumMetric(col.outfitsClicks)
    )
  },
  {
    key: 'outfitsCtr',
    title: getMetricTitle('country', 'outfits_ctr'),
    width: 96,
    align: 'center',
    sort: (
      a: CcmCampaignReportingOutfitPerformanceAll,
      b: CcmCampaignReportingOutfitPerformanceAll
    ) => (
      getSortingFunction()?.outfitsCtr(a, b)
    ),
    render: (col: CcmCampaignReportingOutfitPerformanceAll) => (
      `${getPercentage(col.outfitsCtr, 1)}%`
    )
  },
  {
    key: 'outfitsProductPageViews',
    title: getMetricTitle('country', 'outfits_product_page_views'),
    width: 96,
    align: 'center',
    sort: (
      a: CcmCampaignReportingOutfitPerformanceAll,
      b: CcmCampaignReportingOutfitPerformanceAll
    ) => (
      getSortingFunction()?.outfitsProductPageViews(a, b)
    ),
    render: (col: CcmCampaignReportingOutfitPerformanceAll) => (
      abbrNumMetric(col.outfitsProductPageViews)
    )
  },
  {
    key: 'outfitsNumItemsSold',
    title: getMetricTitle('country', 'outfits_num_items_sold'),
    width: 96,
    align: 'center',
    sort: (
      a: CcmCampaignReportingOutfitPerformanceAll,
      b: CcmCampaignReportingOutfitPerformanceAll
    ) => (
      getSortingFunction()?.outfitsNumItemsSold(a, b)
    ),
    render: (col: CcmCampaignReportingOutfitPerformanceAll) => (
      abbrNumMetric(col.outfitsNumItemsSold)
    )
  },
  {
    key: 'outfitsRevenue',
    title: getMetricTitle('outfit', 'outfits_revenue'),
    width: 88,
    align: 'center',
    sort: (
      a: CcmCampaignReportingOutfitPerformanceAll,
      b: CcmCampaignReportingOutfitPerformanceAll
    ) => (
      getSortingFunction()?.outfitsRevenue(a, b)
    ),
    render: (col: CcmCampaignReportingOutfitPerformanceAll) => (
      renderPrice(col.outfitsRevenue)
    )
  }
]);
